import React from 'react'
import { Box, Button, Link, MenuItem } from '@mui/material'
import { SxProps, Theme } from '@mui/material'
import FormField from 'components/FormField'
import { AdminPracticeCreationForm, AdminPracticeCreationResult, WithContactStrings } from 'types/interfaces'
import update from 'immutability-helper'
import { useAPI } from 'contexts/APIProvider'
import { useSnackBarAlert } from 'contexts/SnackBarAlertProvider'
import AdminPracticeCreationFormError from 'errors/AdminPracticeCreationFormError'
import { MARKETING_CHANNELS, MULTIPLE_EMAILS_HELPER_TEXT, MULTIPLE_PHONES_HELPER_TEXT } from 'types/constants'
import Alert from '@mui/material/Alert'
import { PaymentOption } from 'types/types'
import CQCLocationSelector from 'components/CQCLocationSelector'
import CQCLocation, { CQC_PREFILLED_FIELDS } from 'logic/CQCLocation'
import ManualAddressFields from 'components/ManualAddressFields'
import { adminPracticeDetailURL } from 'routes/urls'
import { trim } from 'lodash'
import { toContactLists } from 'logic/helpers'

const style: SxProps<Theme> = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: ['80%', '80%', '50%'],
    maxHeight: '90%',
    bgcolor: 'background.default',
    border: '2px solid #000',
    boxShadow: 24,
    padding: 1,
    overflow: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 1
}

interface CreatePracticeModalProps {
  onCancel: () => void
}

const CreatePracticeModal: React.FC<CreatePracticeModalProps> = ({
  onCancel
}) => {
    const { api } = useAPI()
    const { showAlert } = useSnackBarAlert()
    const [form, setForm] = React.useState<WithContactStrings<AdminPracticeCreationForm>>({
        tz: 'Europe/London',
        invoice_due_days: 1,
        default_settlement_days: 15,
        start_time: '09:00',
        end_time: '17:00',
        lunch_break: 60,
        description: '',
    })
    const [errors, setErrors] = React.useState<AdminPracticeCreationFormError>()
    const [loading, setLoading] = React.useState<boolean>(false)
    const [result, setResult] = React.useState<AdminPracticeCreationResult>()
    const [cqcLocation, setCqcLocation] = React.useState<CQCLocation | null>(null)

    const selectCqcLocation = React.useCallback((location: CQCLocation | null) => {
        setCqcLocation(location)
        setForm(prev => update(prev, location ? { $merge: location.toPracticeForm() } : { $unset: CQC_PREFILLED_FIELDS }))
        setErrors(prev => prev && update(prev, { cqc_location_id: { $set: [] } }))
    }, [])

    const clearError = React.useCallback((name: string) => {
        setErrors(prev => prev && update(prev, { [name]: { $set: [] } }))
    }, [])

    const updateForm = React.useCallback((name: string, value: any) => {
        setForm(update(form, { [name]: { $set: value } }))
        if (errors) setErrors(update(errors, { [name]: { $set: [] } }))
    }, [form, errors])

    const save = React.useCallback(async () => {
        try {
            setLoading(true)
            const result = await api.adminCreatePractice(toContactLists({
                ...form,
                email_for_inquiries: form.email_for_inquiries?.toLowerCase(),
                email_for_notifications: form.email_for_notifications?.toLowerCase(),
            }))
            setResult(result)
        } catch (e) {
            if (e instanceof AdminPracticeCreationFormError) {
                setErrors(e)
            } else {
                showAlert('error', 'Error creating practice')
                console.error('unhandled exception', e)
            }
        } finally {
            setLoading(false)
        }
    }, [api, form, showAlert, setResult])

    const paymentOptions: { value: PaymentOption; label: string }[] = [
        { value: 'invoice_weekly_pay_manually', label: 'Invoice Weekly Pay Manually' },
        { value: 'invoice_biweekly_pay_manually', label: 'Invoice Biweekly Pay Manually' },
        { value: 'invoice_monthly_pay_manually', label: 'Invoice Monthly Pay Manually' },
    ]

    return (
        <Box sx={style}>
            <CQCLocationSelector
                label='Enter your practice postcode to find your practice (CQC registered)'
                helperText='Type the full postcode and select the CQC location to prefill the practice name and address. The fields below override the prefilled values.'
                value={cqcLocation}
                onChange={selectCqcLocation}
                errors={errors?.cqc_location_id}
            />
            <FormField
                name='name'
                label='Practice Name'
                value={form.name ?? ''}
                onChange={(e) => updateForm('name', trim(e.target.value ?? ''))}
                errors={errors?.name}
            />
            <ManualAddressFields
                form={form}
                error={errors}
                setForm={setForm}
                clearError={clearError}
            />
            <FormField
                name='parent_company'
                label='Practice Group Name'
                helperText='If you are part of a group of practices, enter the name of the group here. If you are independent, leave this blank.'
                value={form.parent_company ?? ''}
                onChange={(e) => updateForm('parent_company', trim(e.target.value ?? ''))}
                errors={errors?.parent_company}
            />
            <FormField
                name='email'
                label='User Email'
                value={form.email ?? ''}
                onChange={(e) => updateForm('email', trim(e.target.value ?? '').toLowerCase())}
                errors={errors?.email}
            />
            <FormField
                name='first_name'
                label='User First Name'
                value={form.first_name ?? ''}
                onChange={(e) => updateForm('first_name', trim(e.target.value ?? ''))}
                errors={errors?.first_name}
            />
            <FormField
                name='last_name'
                label='User Last Name'
                value={form.last_name ?? ''}
                onChange={(e) => updateForm('last_name', trim(e.target.value ?? ''))}
                errors={errors?.last_name}
            />            
            <FormField
                name='affiliate_code'
                label='Affiliate Code'
                value={form.affiliate_code ?? ''}
                onChange={(e) => updateForm('affiliate_code', trim(e.target.value ?? ''))}
                errors={errors?.affiliate_code}
            />
            <FormField
                select
                name='marketing_channel'
                label='How did you hear about Locumloop?'
                value={form.marketing_channel ?? ''}
                onChange={(e) => updateForm('marketing_channel', e.target.value)}
                errors={errors?.marketing_channel}
            >
              <MenuItem value=''>Select Channel</MenuItem>
              {MARKETING_CHANNELS.map(option => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
            </FormField>
            <FormField
                name='email_for_inquiries'
                label='Email (General Inquiries)'
                value={form.email_for_inquiries ?? ''}
                onChange={(e) => updateForm('email_for_inquiries', e.target.value)}
                errors={errors?.email_for_inquiries}
                helperText={MULTIPLE_EMAILS_HELPER_TEXT}
            />
            <FormField
                name='email_for_notifications'
                label='Email (to receive notifications)'
                value={form.email_for_notifications ?? ''}
                onChange={(e) => updateForm('email_for_notifications', e.target.value)}
                errors={errors?.email_for_notifications}
                helperText={MULTIPLE_EMAILS_HELPER_TEXT}
            />
            <FormField
                name='phone_for_inquiries'
                label='Phone Number (General Inquiries)'
                value={form.phone_for_inquiries ?? ''}
                onChange={(e) => updateForm('phone_for_inquiries', e.target.value)}
                errors={errors?.phone_for_inquiries}
                helperText={MULTIPLE_PHONES_HELPER_TEXT}
            />
            <FormField
                name='phone_for_notifications'
                label='Phone Number (to receive SMS notifications)'
                value={form.phone_for_notifications ?? ''}
                onChange={(e) => updateForm('phone_for_notifications', e.target.value)}
                errors={errors?.phone_for_notifications}
                helperText={MULTIPLE_PHONES_HELPER_TEXT}
            />
            <FormField
                name='billing_name'
                label='Billing Name'
                value={form.billing_name ?? ''}
                onChange={(e) => updateForm('billing_name', trim(e.target.value ?? ''))}
                errors={errors?.billing_name}
            />
            <FormField
                name='billing_email'
                label='Billing Email'
                value={form.billing_email ?? ''}
                onChange={(e) => updateForm('billing_email', trim(e.target.value ?? '').toLowerCase())}
                errors={errors?.billing_email}
            />
            <FormField
                select
                name='payment_option'
                label='Payment Option'
                value={form.payment_option ?? ''}
                onChange={(e) => updateForm('payment_option', e.target.value)}
                errors={errors?.payment_option}
            >
              <MenuItem value=''>Select Payment Option</MenuItem>
              {paymentOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </FormField>
            <FormField
                name='invoice_due_days'
                label='Invoice Due Days'
                type='number'
                value={form.invoice_due_days ?? 1}
                onChange={(e) => updateForm('invoice_due_days', e.target.value ? Number(e.target.value) : undefined)}
                errors={errors?.invoice_due_days}
                helperText="The due date printed on invoices for this practice"
            />
            <FormField
                name='default_settlement_days'
                label='Expected Settlement Days'
                type='number'
                value={form.default_settlement_days ?? 15}
                onChange={(e) => updateForm('default_settlement_days', e.target.value ? Number(e.target.value) : undefined)}
                errors={errors?.default_settlement_days}
                helperText="The realistic expected days that the practice will pay the invoice after receiving it. This is to set the expectation of the nurses"
            />
            { result ? (
                <Alert severity="success" variant='filled' sx={{ mb: 2 }}>
                    <div>Practice created successfully!</div>
                    <div>Practice ID: <Link href={adminPracticeDetailURL(result.practice_id)}>{result.practice_id}</Link></div>
                    <div>User Email: <b>{result.user_email}</b></div>
                    <div>User Password: <b>{result.user_password}</b></div>
                </Alert>
            ) : (
                <>
                    <Button variant='contained' color='primary' onClick={save} disabled={loading} sx={{ mt: 2 }}>
                        {loading ? 'Please wait...' : 'Create Practice'}
                    </Button>
                    <Button variant='outlined' color='primary' onClick={onCancel} disabled={loading} sx={{ mt: 1 }}>
                        Cancel
                    </Button>
                </>
            ) }
        </Box>
    )
}

export default CreatePracticeModal